import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { listAdminSupportTickets, type AdminSupportTicket } from "@/lib/support.functions";
import { explainPolicyCheckSafe } from "@/lib/breadcrumbs";

export const Route = createFileRoute("/_authenticated/admin/support")({
  head: () => ({
    meta: [
      { title: "Support inbox — Admin | The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Triage incoming support tickets with diagnostics: missing profile checks, blocked policy, user and shift references.",
      },
    ],
  }),
  component: AdminSupportInbox,
});

const STATUSES = ["all", "open", "in_progress", "resolved", "closed"] as const;
type StatusFilter = (typeof STATUSES)[number];

function AdminSupportInbox() {
  const load = useServerFn(listAdminSupportTickets);
  const [tickets, setTickets] = useState<AdminSupportTicket[]>([]);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = async (s: StatusFilter = status, q = search) => {
    setLoading(true);
    try {
      const rows = await load({ data: { status: s, search: q || null, limit: 100 } });
      setTickets(rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh("all", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
        <h1 className="font-display text-2xl tracking-wide-luxury">Support inbox</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Incoming tickets with their diagnostics — missing profile checks, the blocked policy and
          what it means, plus user, shift and booking references.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatus(s);
                void refresh(s, search);
              }}
              aria-pressed={status === s}
              className={`border px-3 py-1.5 text-xs tracking-wide-luxury ${
                status === s ? "border-accent text-accent" : "border-border hover:border-accent"
              }`}
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
          <form
            className="ml-auto flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void refresh(status, search);
            }}
          >
            <label className="sr-only" htmlFor="ticket-search">
              Search tickets
            </label>
            <Input
              id="ticket-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ticket code or subject"
              className="h-9 w-56"
            />
            <Button type="submit" size="sm" variant="outline">
              Search
            </Button>
          </form>
        </div>

        {loading ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading tickets…</p>
        ) : tickets.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">No tickets match this filter.</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {tickets.map((t) => (
              <li key={t.id} className="border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <Link
                      to="/support/$code"
                      params={{ code: t.ticket_code }}
                      className="font-mono text-xs underline underline-offset-2"
                    >
                      {t.ticket_code}
                    </Link>
                    <p className="mt-1 font-display text-sm">{t.subject}</p>
                  </div>
                  <span className="text-[11px] uppercase tracking-wide-luxury text-muted-foreground">
                    {t.status.replace(/_/g, " ")} · {new Date(t.created_at).toLocaleString("en-GB")}
                  </span>
                </div>

                <p className="mt-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                  {t.message.length > 400 ? `${t.message.slice(0, 400)}…` : t.message}
                </p>

                {t.missing_columns.length > 0 ? (
                  <p className="mt-2 border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
                    Missing checks: <span className="font-mono">{t.missing_columns.join(", ")}</span>
                  </p>
                ) : null}

                {t.policy_name ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Failed policy: <code className="font-mono">{t.policy_name}</code> —{" "}
                    {t.policy_explanation ?? explainPolicyCheckSafe(t.policy_name)}
                  </p>
                ) : null}

                <dl className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-2">
                  <div>
                    <dt className="inline">User: </dt>
                    <dd className="inline">
                      {t.user_name ?? "—"} {t.user_email ? `<${t.user_email}>` : ""}{" "}
                      <code className="font-mono">{t.user_id}</code>
                    </dd>
                  </div>
                  {t.shift_id ? (
                    <div>
                      <dt className="inline">Shift: </dt>
                      <dd className="inline">
                        <Link
                          to="/admin/shifts/$id"
                          params={{ id: t.shift_id }}
                          className="font-mono underline underline-offset-2"
                        >
                          {t.shift_id}
                        </Link>
                      </dd>
                    </div>
                  ) : null}
                  {t.booking_id ? (
                    <div>
                      <dt className="inline">Booking: </dt>
                      <dd className="inline font-mono">{t.booking_id}</dd>
                    </div>
                  ) : null}
                  {t.sentry_event_id ? (
                    <div>
                      <dt className="inline">Sentry event: </dt>
                      <dd className="inline font-mono">{t.sentry_event_id}</dd>
                    </div>
                  ) : null}
                  {t.page_url ? (
                    <div>
                      <dt className="inline">Page: </dt>
                      <dd className="inline break-all">{t.page_url}</dd>
                    </div>
                  ) : null}
                  {t.source ? (
                    <div>
                      <dt className="inline">Source: </dt>
                      <dd className="inline">{t.source}</dd>
                    </div>
                  ) : null}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
